/**
 * Profile settings — editable profile header with drag-and-drop avatar upload,
 * a validated details form, and skeleton/loading/success states.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { updateProfile } from '@/src/modules/auth/session';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';
import { SettingsCard, TextField, Skeleton } from '@/src/modules/components/settings/primitives';
import { useToast } from '@/src/modules/components/settings/ToastProvider';

type Form = { fullName: string; username: string; email: string; phone: string };
type Errors = Partial<Record<keyof Form, string>>;

function validate(form: Form): Errors {
  const errors: Errors = {};
  if (!form.fullName.trim()) errors.fullName = 'Full name is required.';
  if (!/^[a-z0-9_]{3,20}$/i.test(form.username))
    errors.username = '3–20 letters, numbers or underscores.';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
    errors.email = 'Enter a valid email address.';
  if (form.phone && !/^[+()\d\s-]{7,20}$/.test(form.phone))
    errors.phone = 'Enter a valid phone number.';
  return errors;
}

/** Down-scale a user-picked image to a small square JPEG data URL.
 *  Same approach as the admin portal: keeps the PATCH payload modest and
 *  fits comfortably under the backend's avatar size cap. */
async function fileToAvatarDataUrl(
  file: File,
  size = 256,
  quality = 0.82,
): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  const edge = Math.min(bitmap.width, bitmap.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    bitmap,
    (bitmap.width - edge) / 2,
    (bitmap.height - edge) / 2,
    edge,
    edge,
    0,
    0,
    size,
    size,
  );
  bitmap.close?.();
  return canvas.toDataURL('image/jpeg', quality);
}

export function ProfileSection() {
  // Seed from the AuthContext profile so the form mounts populated, and
  // route saves back through `setUser` so the sidebar/header avatars
  // re-render the moment the API returns.
  const { user, setUser, refreshProfile } = useAuth();
  const { toast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [hydrating, setHydrating] = useState(!user);
  const [form, setForm] = useState<Form>({
    fullName: user?.displayName ?? '',
    username: user?.username ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [avatar, setAvatar] = useState<string | null>(user?.avatar ?? null);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep the form in sync if the AuthProvider hydrates after we mount.
  useEffect(() => {
    if (!user) return;
    setForm({
      fullName: user.displayName ?? '',
      username: user.username ?? '',
      email: user.email ?? '',
      phone: user.phone ?? '',
    });
    setAvatar(user.avatar ?? null);
    setHydrating(false);
  }, [user]);

  function set<K extends keyof Form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  async function handleFile(file?: File | null) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('Please choose an image file.', 'error');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast('Image must be under 4 MB.', 'error');
      return;
    }
    try {
      // Optimistic local preview: show the new picture instantly, then
      // persist it on Save (single round-trip together with the form).
      const dataUrl = await fileToAvatarDataUrl(file);
      setAvatar(dataUrl);
    } catch {
      toast('Could not process this image.', 'error');
    }
  }

  async function handleSave() {
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast('Please fix the highlighted fields.', 'error');
      return;
    }
    setSaving(true);
    try {
      const fresh = await updateProfile({
        name: form.fullName,
        username: form.username || null,
        email: form.email,
        phone: form.phone || null,
        avatar,
      });
      // Fan out to every consumer of `useAuth().user` — sidebar avatar,
      // header dropdown, dashboard widgets.
      setUser(fresh);
      toast('Profile updated successfully.');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save profile', 'error');
      // Safety net: pull the canonical row in case the PATCH partially
      // applied (e.g. avatar saved, username rejected on conflict).
      void refreshProfile();
    } finally {
      setSaving(false);
    }
  }

  const initials =
    form.fullName
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'U';

  return (
    <div className="flex flex-col gap-6">
      {/* Editable profile header */}
      <SettingsCard>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div
            onClick={() => fileInput.current?.click()}
            onDragOver={(e: any) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e: any) => {
              e.preventDefault();
              setDragging(false);
              handleFile(e.dataTransfer?.files?.[0]);
            }}
            className={cn(
              'group relative w-24 h-24 shrink-0 rounded-full cursor-pointer overflow-hidden',
              'ring-4 transition-all',
              dragging ? 'ring-primary' : 'ring-surface-low',
            )}
          >
            {hydrating ? (
              <Skeleton className="w-full h-full rounded-full" />
            ) : avatar ? (
              <img src={avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-primary/10 text-primary flex items-center justify-center text-2xl font-bold">
                {initials}
              </div>
            )}
            <div className="absolute inset-0 bg-on-surface/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
              <Icons.Camera size={20} />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            {hydrating ? (
              <>
                <Skeleton className="h-5 w-40 mb-2" />
                <Skeleton className="h-4 w-56" />
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-on-surface truncate">
                  {form.fullName || 'Your name'}
                </h3>
                <p className="text-sm text-on-surface-variant truncate">
                  @{form.username || 'username'} · {form.email}
                </p>
              </>
            )}
            <p className="text-xs text-on-surface-variant mt-2">
              Drag &amp; drop an image here, or click your avatar. PNG/JPG, max 4 MB.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInput.current?.click()}
              disabled={hydrating}
            >
              <Icons.Upload size={15} className="mr-1.5" />
              Upload
            </Button>
            {avatar && (
              <Button
                variant="ghost"
                size="sm"
                className="text-status-failed"
                onClick={() => setAvatar(null)}
              >
                Remove
              </Button>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e: any) => handleFile(e.target.files?.[0])}
          />
        </div>
      </SettingsCard>

      {/* Details form */}
      <SettingsCard
        icon={Icons.User}
        title="Personal information"
        description="This information appears on your account and invitations."
      >
        {hydrating ? (
          <div className="grid sm:grid-cols-2 gap-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid sm:grid-cols-2 gap-5"
          >
            <TextField
              id="profile-name"
              label="Full name"
              icon={Icons.User}
              value={form.fullName}
              error={errors.fullName}
              onChange={(e: any) => set('fullName', e.target.value)}
            />
            <TextField
              id="profile-username"
              label="Username"
              value={form.username}
              error={errors.username}
              hint="invitecloud.app/@username"
              onChange={(e: any) => set('username', e.target.value)}
            />
            <TextField
              id="profile-email"
              label="Email address"
              icon={Icons.Mail}
              type="email"
              value={form.email}
              error={errors.email}
              onChange={(e: any) => set('email', e.target.value)}
            />
            <TextField
              id="profile-phone"
              label="Phone number"
              icon={Icons.Device}
              value={form.phone}
              error={errors.phone}
              placeholder="+1 555 000 1234"
              onChange={(e: any) => set('phone', e.target.value)}
            />
          </motion.div>
        )}

        <div className="flex justify-end mt-6 pt-5 border-t border-outline-variant/15">
          <Button onClick={handleSave} loading={saving} disabled={hydrating || saving}>
            Save changes
          </Button>
        </div>
      </SettingsCard>
    </div>
  );
}
